import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import DatePicker from './DatePicker';
import SunbedBooking from './SunbedBooking';
import BreakfastBooking from './BreakfastBooking';
import RoomsPage from './RoomsPage';
import DinnerBooking from './DinnerBooking';
import AdminLogin from './AdminLogin';
import AdminPanel from './AdminPanel';
import PaymentSuccess from './PaymentSuccess';
import PaymentFail from './PaymentFail';
import OurRooms from './OurRooms';
import ReservationPage from './ReservationPage';
import ReservationLookup from './ReservationLookup';
import PaymentPage from './PaymentPage';
import { publicAPI, productsAPI } from './services/api';
import MultiRoomCalendar from './MultiRoomCalendar';
import BookingFormModal from './BookingFormModal';
import ReservationCalendar from './ReservationCalendar';

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<'home' | 'sunbed' | 'breakfast' | 'rooms' | 'our-rooms' | 'reservation' | 'dinner' | 'admin' | 'payment-success' | 'payment-fail' | 'reservation-lookup' | 'payment' | 'calendar'>('home');
  const [paymentReservationId, setPaymentReservationId] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [childrenCount, setChildrenCount] = useState(0);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [homepageData, setHomepageData] = useState<{
    about: any; sections: any[]; header: any; footer: any;
    policies: any[]; contact: any; slides: any[];
  } | null>(null);
  const [policyModal, setPolicyModal] = useState<{ title: string; content: string } | null>(null);
  const [slidesLoaded, setSlidesLoaded] = useState<boolean[]>([]);
  const [dinnerWebEnabled, setDinnerWebEnabled] = useState(true);
  const [sunbedWebEnabled, setSunbedWebEnabled] = useState(true);

  const syncDinnerWebEnabled = () => {
    productsAPI.getPublicSetting('dinner_public')
      .then((res) => {
        const value = res.data?.data?.value;
        setDinnerWebEnabled(value?.enabled !== false);
      })
      .catch(() => {
        setDinnerWebEnabled(true);
      });
  };

  const syncSunbedWebEnabled = () => {
    productsAPI.getPublicSetting('sunbed_public')
      .then((res) => {
        const value = res.data?.data?.value;
        setSunbedWebEnabled(value?.enabled !== false);
      })
      .catch(() => {
        setSunbedWebEnabled(true);
      });
  };
  
  // Form state'leri
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [adultsCount, setAdultsCount] = useState('2');
  const [childrenAges, setChildrenAges] = useState<number[]>([]);
  
  // Quick Booking Modal state
  const [showQuickBooking, setShowQuickBooking] = useState(false);

  // Homepage takvim dropdown state
  const [showHomeDatePicker, setShowHomeDatePicker] = useState(false);
  const homeDatePickerRef = useRef<HTMLDivElement>(null);
  const [quickBookingData, setQuickBookingData] = useState<{
    roomId: string;
    roomName: string;
    checkInDate: string;
    checkOutDate: string;
    adults: number;
    children: number;
    childAges: number[];
    totalPrice: number;
    nights: number;
  } | null>(null);

  // Admin login durumunu kontrol et
  useEffect(() => {
    const loggedIn = localStorage.getItem('isAdminLoggedIn') === 'true';
    setIsAdminLoggedIn(loggedIn);
    
    // URL'den sayfayı belirle - hem hash hem path kontrol et
    const hash = window.location.hash.substring(1); // # işaretini kaldır
    const pathname = window.location.pathname;
    
    // Path tabanlı routing (PayTR yönlendirmesi için)
    if (pathname === '/payment-success') {
      setCurrentPage('payment-success');
      return;
    } else if (pathname === '/payment-fail') {
      setCurrentPage('payment-fail');
      return;
    } else if (pathname.startsWith('/odeme/')) {
      const resId = pathname.replace('/odeme/', '');
      if (resId) {
        setPaymentReservationId(resId);
        setCurrentPage('payment');
        return;
      }
    }
    
    // Hash tabanlı routing
    const handleHash = (h: string) => {
      if (h === 'payment-success') setCurrentPage('payment-success');
      else if (h === 'payment-fail') setCurrentPage('payment-fail');
      else if (h === 'rooms') setCurrentPage('rooms');
      else if (h === 'our-rooms') setCurrentPage('our-rooms');
      else if (h === 'reservation') { setCurrentPage('reservation'); window.scrollTo(0, 0); }
      else if (h === 'reservation-lookup') setCurrentPage('reservation-lookup');
      else if (h === 'admin') setCurrentPage('admin');
      else if (h === 'calendar') { setCurrentPage('calendar'); window.scrollTo(0, 0); }
      else if (h === 'home' || h === '') setCurrentPage('home');
    };

    handleHash(hash);

    // Hash değişikliklerini dinle (sayfa içi navigasyon için)
    const onHashChange = () => {
      const newHash = window.location.hash.substring(1);
      handleHash(newHash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // AsistOmni webchat widget'tan gelen rezervasyon modal açma komutunu dinle
  useEffect(() => {
    const handleAsistOmniMessage = (event: MessageEvent) => {
      if (!event.data || event.data.type !== 'open_reservation_modal') return;
      const { checkIn, checkOut, adults, children, childrenAges: ages, roomTypeId } = event.data.data || {};
      if (checkIn) setCheckInDate(checkIn);
      if (checkOut) setCheckOutDate(checkOut);
      if (adults) setAdultsCount(String(adults));
      if (children !== undefined) {
        setChildrenCount(Number(children));
        setChildrenAges(Array.isArray(ages) ? ages : []);
      }
      // roomTypeId varsa sessionStorage'a yaz — ReservationPage arama bitince bu odayı otomatik seçip formu açar
      if (roomTypeId) {
        sessionStorage.setItem('preSelectedRoomId', roomTypeId);
      }
      setCurrentPage('reservation');
      window.scrollTo(0, 0);
    };
    window.addEventListener('message', handleAsistOmniMessage);
    return () => window.removeEventListener('message', handleAsistOmniMessage);
  }, []);

  const slides: string[] = (homepageData?.slides && homepageData.slides.length > 0)
    ? homepageData.slides.map((s: any) => s.image_url)
    : ['/slide2.jpg', '/slide3.jpg', '/slide1.jpg', '/slide4.jpg'];

  // Resimleri önceden yükle (preload)
  React.useEffect(() => {
    setSlidesLoaded(new Array(slides.length).fill(false));
    slides.forEach((src, i) => {
      const img = new Image();
      img.onload = () => setSlidesLoaded(prev => {
        const next = [...prev];
        next[i] = true;
        return next;
      });
      img.src = src;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length]);

  // Otomatik slider
  React.useEffect(() => {
    if (currentPage === 'home') {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [currentPage, slides.length]);

  const handleChildrenCountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const count = parseInt(e.target.value);
    setChildrenCount(count);
    setChildrenAges(prev => {
      if (count > prev.length) {
        return [...prev, ...Array(count - prev.length).fill(7)];
      } else {
        return prev.slice(0, count);
      }
    });
  };

  const handleAdultsCountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const count = parseInt(e.target.value);
    setAdultsCount(count.toString());
  };

  const handleChildAgeChange = (index: number, age: number) => {
    setChildrenAges(prev => {
      const newAges = [...prev];
      newAges[index] = age;
      return newAges;
    });
  };

  const rooms = [
    {
      id: 1,
      name: 'Deluxe Oda',
      price: '₺2,500',
      description: 'Lüks ve konforlu, şehir manzaralı geniş odalar',
      features: ['Çift Kişilik Yatak', 'Balkon', 'Şehir Manzarası', 'Mini Bar']
    },
    {
      id: 2,
      name: 'Süit Oda',
      price: '₺4,500',
      description: 'Eşsiz konfora sahip premium süit odalar',
      features: ['King Size Yatak', 'Oturma Alanı', 'Jakuzi', 'VIP Hizmet']
    },
    {
      id: 3,
      name: 'Standart Oda',
      price: '₺1,800',
      description: 'Konforlu ve ekonomik seçenekler',
      features: ['Çift/Tek Kişilik Yatak', 'Klima', 'TV', 'Ücretsiz WiFi']
    }
  ];



  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMenuOpen(false);
    }
  };

  const navigateToSunbed = () => {
    if (!sunbedWebEnabled) {
      alert('Şezlong online satışı şu anda kapalıdır.');
      return;
    }
    setCurrentPage('sunbed');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navigateToBreakfast = () => {
    setCurrentPage('breakfast');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navigateToRooms = () => {
    setCurrentPage('rooms');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navigateToOurRooms = () => {
    setCurrentPage('our-rooms');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navigateToReservationDirect = () => {
    // Menüden gelindiğinde tarih bilgilerini sıfırla
    setCheckInDate('');
    setCheckOutDate('');
    setAdultsCount('2');
    setChildrenCount(0);
    setChildrenAges([]);
    setCurrentPage('reservation');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navigateToReservationWithSearch = () => {
    // Form'dan gelindiğinde tarihler zaten state'de - sıfırlama
    setCurrentPage('reservation');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navigateToDinner = () => {
    if (!dinnerWebEnabled) {
      alert('Akşam yemeği online satışı şu anda kapalıdır.');
      return;
    }
    setCurrentPage('dinner');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navigateToAdmin = () => {
    setCurrentPage('admin');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navigateToCalendar = () => {
    setCurrentPage('calendar');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navigateToHome = () => {
    setCurrentPage('home');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  // Homepage date picker dışına tıklanınca kapat
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (homeDatePickerRef.current && !homeDatePickerRef.current.contains(e.target as Node)) {
        setShowHomeDatePicker(false);
      }
    };
    if (showHomeDatePicker) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHomeDatePicker]);

  const navigateToReservationLookup = () => {
    setCurrentPage('reservation-lookup');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleHomeCalendarReservationSelect = (data: {
    roomId: string;
    roomName: string;
    checkInDate: string;
    checkOutDate: string;
    adults: number;
    children: number;
    childAges: number[];
    totalPrice: number;
    nights: number;
  }) => {
    // Direkt rezervasyon formunu aç
    setQuickBookingData(data);
    setShowQuickBooking(true);
    setMenuOpen(false);
  };

  // Ana sayfa içeriğini yükle
  useEffect(() => {
    const apiBase = (() => {
      if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
      if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') return '/api';
      return `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:5001/api`;
    })();
    fetch(`${apiBase}/homepage`)
      .then(r => r.json())
      .then(data => { if (data.success) setHomepageData(data.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    syncDinnerWebEnabled();
    syncSunbedWebEnabled();

    // Sekme aktif olunca güncel ayarı yeniden çek
    const onFocus = () => {
      syncDinnerWebEnabled();
      syncSunbedWebEnabled();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncDinnerWebEnabled();
        syncSunbedWebEnabled();
      }
    };

    // Admin sekmesinden gelen localStorage yayınına anlık tepki ver
    const onStorage = (event: StorageEvent) => {
      if (!event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue);
        if (event.key === 'dinnerPublicSettings') {
          setDinnerWebEnabled(parsed?.enabled !== false);
        }
        if (event.key === 'sunbedPublicSettings') {
          setSunbedWebEnabled(parsed?.enabled !== false);
        }
      } catch {
        if (event.key === 'dinnerPublicSettings') syncDinnerWebEnabled();
        if (event.key === 'sunbedPublicSettings') syncSunbedWebEnabled();
      }
    };

    const onDinnerPublicChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      const enabled = customEvent?.detail?.enabled;
      if (typeof enabled === 'boolean') {
        setDinnerWebEnabled(enabled);
        return;
      }
      syncDinnerWebEnabled();
    };

    const onSunbedPublicChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      const enabled = customEvent?.detail?.enabled;
      if (typeof enabled === 'boolean') {
        setSunbedWebEnabled(enabled);
        return;
      }
      syncSunbedWebEnabled();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    window.addEventListener('dinner-public-settings-changed', onDinnerPublicChanged);
    window.addEventListener('sunbed-public-settings-changed', onSunbedPublicChanged);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('dinner-public-settings-changed', onDinnerPublicChanged);
      window.removeEventListener('sunbed-public-settings-changed', onSunbedPublicChanged);
    };
  }, []);

  // Aktif kullanıcı takibi — 30sn'de bir heartbeat gönder (admin sayfasında gönderme)
  useEffect(() => {
    if (currentPage === 'admin') return;
    const apiBase = (() => {
      if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
      if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') return '/api';
      return `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:5001/api`;
    })();
    // Session ID — tab başına, sekme kapanınca gider
    let sid = sessionStorage.getItem('_hb_sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('_hb_sid', sid);
    }
    const send = () => {
      fetch(`${apiBase}/admin/analytics/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sid, page: currentPage }),
      }).catch(() => {});
    };
    send(); // hemen bir kez gönder
    const iv = setInterval(send, 30_000);
    return () => clearInterval(iv);
  }, [currentPage]);

  // Webchat widget'ı genel ayarlara göre dinamik yükle
  useEffect(() => {
    const SCRIPT_ID = 'asistomni-webchat-widget';
    const FALLBACK_AVATAR = '/webchat-avatar.svg';
    let observer: MutationObserver | null = null;

    const syncWidgetToggleState = () => {
      const toggle = document.getElementById('asistomni-toggle');
      if (!toggle) return;

      const iconClose = document.getElementById('asistomni-icon-close');
      const closeVisible = !!iconClose && window.getComputedStyle(iconClose).display !== 'none';
      toggle.setAttribute('data-open', closeVisible ? 'true' : 'false');
      toggle.setAttribute('data-live-enhanced', 'true');
    };

    const removeWidgetScript = () => {
      const existingScript = document.getElementById(SCRIPT_ID);
      if (existingScript) {
        existingScript.remove();
      }
    };

    const loadWidget = async () => {
      try {
        const response = await publicAPI.getGeneralSettings();
        const raw = response.data?.data;
        const settings = raw?.value || raw || {};
        const webchat = settings.webchat || {};

        const enabled = !!webchat.enabled;
        const channelId = String(webchat.channel_id || '').trim();
        const scriptUrl = String(webchat.script_url || 'https://asistomni.com/widget.js').trim();

        if (!enabled || !channelId || !scriptUrl.startsWith('https://asistomni.com/')) {
          removeWidgetScript();
          return;
        }

        const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
        if (existing) {
          const currentChannel = existing.getAttribute('data-channel-id') || '';
          const currentSrc = existing.getAttribute('src') || '';
          if (currentChannel === channelId && currentSrc === scriptUrl) {
            return;
          }
          existing.remove();
        }

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = scriptUrl;
        script.async = true;
        script.setAttribute('data-channel-id', channelId);
        document.body.appendChild(script);

        // Widget DOM'u sonradan eklendiği için kısa gecikmeyle durum senkronize edilir.
        window.setTimeout(syncWidgetToggleState, 250);
      } catch (error) {
        console.warn('Webchat ayarları yüklenemedi:', error);
      }
    };

    const handleWidgetImageError = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!(target instanceof HTMLImageElement)) return;

      // Sadece AsistOmni widget içindeki kırık görsellere fallback uygula.
      const widgetRoot = document.getElementById('asistomni-widget');
      if (!widgetRoot || !widgetRoot.contains(target)) return;

      if (target.dataset.fallbackApplied === '1') return;
      target.dataset.fallbackApplied = '1';
      target.src = FALLBACK_AVATAR;
      target.style.objectFit = 'cover';
    };

    document.addEventListener('error', handleWidgetImageError, true);
    observer = new MutationObserver(() => {
      syncWidgetToggleState();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    loadWidget();

    return () => {
      document.removeEventListener('error', handleWidgetImageError, true);
      if (observer) observer.disconnect();
    };
  }, []);

  const getButtonHandler = (action: string): (() => void) | null => {
    switch (action) {
      case 'rooms': return navigateToRooms;
      case 'our-rooms': return navigateToOurRooms;
      case 'sunbed': return sunbedWebEnabled ? navigateToSunbed : null;
      case 'breakfast': return navigateToBreakfast;
      case 'dinner': return dinnerWebEnabled ? navigateToDinner : null;
      default: return null;
    }
  };

  const openPolicy = async (key: string, title: string) => {
    const apiBase = (() => {
      if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
      if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') return '/api';
      return `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:5001/api`;
    })();
    try {
      const res = await fetch(`${apiBase}/homepage/policies/${key}`);
      const data = await res.json();
      if (data.success) {
        setPolicyModal({ title: data.data.title, content: data.data.content || 'İçerik henüz eklenmemiştir.' });
      }
    } catch {
      setPolicyModal({ title, content: 'İçerik yüklenirken hata oluştu.' });
    }
  };

  // Reservation Lookup sayfası
  if (currentPage === 'reservation-lookup') {
    return <ReservationLookup onBack={navigateToHome} />;
  }

  // Admin sayfası
  if (currentPage === 'admin') {
    if (!isAdminLoggedIn) {
      return <AdminLogin onLogin={() => setIsAdminLoggedIn(true)} onBackToHome={navigateToHome} />;
    }
    return <AdminPanel />;
  }

  if (currentPage === 'sunbed') {
    if (!sunbedWebEnabled) {
      return (
        <div className="App" style={{ padding: '3rem 1rem', textAlign: 'center' }}>
          <h2>Şezlong online satışı şu anda kapalıdır.</h2>
          <button className="btn-feature" onClick={navigateToHome} style={{ marginTop: '1rem' }}>Ana Sayfaya Dön</button>
        </div>
      );
    }
    return (
      <>
        <nav className="navbar">
          <div className="container">
            <div className="nav-brand" onClick={navigateToHome} style={{ cursor: 'pointer' }}>
              <img src="/logo.png" alt="Otel Logo" className="logo" />
              Villa Polikne
            </div>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              ☰
            </button>
            <ul className={`nav-menu ${menuOpen ? 'active' : ''}`}>
              <li><a href="#home" onClick={navigateToHome}>Ana Sayfa</a></li>
              <li><a href="#our-rooms" onClick={navigateToOurRooms}>Odalarımız</a></li>
              <li><a href="#reservation" onClick={navigateToReservationDirect}>Rezervasyon Yap</a></li>
              <li><a href="#calendar" onClick={navigateToCalendar}>Oda Takvimi</a></li>
              {sunbedWebEnabled && <li><a href="#sunbed" onClick={navigateToSunbed} style={{ color: '#d4af37' }}>Şezlong Kiralama</a></li>}
              <li><a href="#breakfast" onClick={navigateToBreakfast}>Kahvaltı</a></li>
              {dinnerWebEnabled && <li><a href="#dinner" onClick={navigateToDinner}>Akşam Yemeği</a></li>}
            </ul>
          </div>
        </nav>
        <SunbedBooking />
      </>
    );
  }

  if (currentPage === 'breakfast') {
    return (
      <>
        <nav className="navbar">
          <div className="container">
            <div className="nav-brand" onClick={navigateToHome} style={{ cursor: 'pointer' }}>
              <img src="/logo.png" alt="Otel Logo" className="logo" />
              Villa Polikne
            </div>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              ☰
            </button>
            <ul className={`nav-menu ${menuOpen ? 'active' : ''}`}>
              <li><a href="#home" onClick={navigateToHome}>Ana Sayfa</a></li>
              <li><a href="#our-rooms" onClick={navigateToOurRooms}>Odalarımız</a></li>
              <li><a href="#reservation" onClick={navigateToReservationDirect}>Rezervasyon Yap</a></li>
              <li><a href="#calendar" onClick={navigateToCalendar}>Oda Takvimi</a></li>
              {sunbedWebEnabled && <li><a href="#sunbed" onClick={navigateToSunbed}>Şezlong Kiralama</a></li>}
              <li><a href="#breakfast" onClick={navigateToBreakfast} style={{ color: '#d4af37' }}>Kahvaltı</a></li>
              {dinnerWebEnabled && <li><a href="#dinner" onClick={navigateToDinner}>Akşam Yemeği</a></li>}
            </ul>
          </div>
        </nav>
        <BreakfastBooking />
      </>
    );
  }

  if (currentPage === 'payment') {
    return <PaymentPage reservationId={paymentReservationId} />;
  }

  if (currentPage === 'payment-success') {
    return <PaymentSuccess />;
  }

  if (currentPage === 'payment-fail') {
    return <PaymentFail />;
  }

  if (currentPage === 'rooms') {
    return (
      <>
        <nav className="navbar">
          <div className="container">
            <div className="nav-brand" onClick={navigateToHome} style={{ cursor: 'pointer' }}>
              <img src="/logo.png" alt="Otel Logo" className="logo" />
              Villa Polikne
            </div>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              ☰
            </button>
            <ul className={`nav-menu ${menuOpen ? 'active' : ''}`}>
              <li><a href="#home" onClick={navigateToHome}>Ana Sayfa</a></li>
              <li><a href="#rooms" onClick={navigateToRooms} style={{ color: '#d4af37' }}>Odalar</a></li>
              <li><a href="#calendar" onClick={navigateToCalendar}>Oda Takvimi</a></li>
              {sunbedWebEnabled && <li><a href="#sunbed" onClick={navigateToSunbed}>Şezlong Kiralama</a></li>}
              <li><a href="#breakfast" onClick={navigateToBreakfast}>Kahvaltı</a></li>
              {dinnerWebEnabled && <li><a href="#dinner" onClick={navigateToDinner}>Akşam Yemeği</a></li>}
            </ul>
          </div>
        </nav>
        <RoomsPage 
          checkIn={checkInDate}
          checkOut={checkOutDate}
          adults={parseInt(adultsCount)}
          children={childrenAges.length}
          childrenAges={childrenAges}
        />
      </>
    );
  }

  if (currentPage === 'our-rooms') {
    return (
      <>
        <nav className="navbar">
          <div className="container">
            <div className="nav-brand" onClick={navigateToHome} style={{ cursor: 'pointer' }}>
              <img src="/logo.png" alt="Otel Logo" className="logo" />
              Villa Polikne
            </div>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              ☰
            </button>
            <ul className={`nav-menu ${menuOpen ? 'active' : ''}`}>
              <li><a href="#home" onClick={navigateToHome}>Ana Sayfa</a></li>
              <li><a href="#our-rooms" onClick={navigateToOurRooms} style={{ color: '#d4af37' }}>Odalarımız</a></li>
              <li><a href="#reservation" onClick={navigateToReservationDirect}>Rezervasyon Yap</a></li>
              <li><a href="#calendar" onClick={navigateToCalendar}>Oda Takvimi</a></li>
              {sunbedWebEnabled && <li><a href="#sunbed" onClick={navigateToSunbed}>Şezlong Kiralama</a></li>}
              <li><a href="#breakfast" onClick={navigateToBreakfast}>Kahvaltı</a></li>
              {dinnerWebEnabled && <li><a href="#dinner" onClick={navigateToDinner}>Akşam Yemeği</a></li>}
            </ul>
          </div>
        </nav>
        <OurRooms onNavigateToReservation={navigateToReservationDirect} />
      </>
    );
  }

  if (currentPage === 'reservation') {
    return (
      <>
        <nav className="navbar">
          <div className="container">
            <div className="nav-brand" onClick={navigateToHome} style={{ cursor: 'pointer' }}>
              <img src="/logo.png" alt="Otel Logo" className="logo" />
              Villa Polikne
            </div>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              ☰
            </button>
            <ul className={`nav-menu ${menuOpen ? 'active' : ''}`}>
              <li><a href="#home" onClick={navigateToHome}>Ana Sayfa</a></li>
              <li><a href="#our-rooms" onClick={navigateToOurRooms}>Odalarımız</a></li>
              <li><a href="#reservation" onClick={navigateToReservationDirect} style={{ color: '#d4af37' }}>Rezervasyon Yap</a></li>
              <li><a href="#calendar" onClick={navigateToCalendar}>Oda Takvimi</a></li>
              {sunbedWebEnabled && <li><a href="#sunbed" onClick={navigateToSunbed}>Şezlong Kiralama</a></li>}
              <li><a href="#breakfast" onClick={navigateToBreakfast}>Kahvaltı</a></li>
              {dinnerWebEnabled && <li><a href="#dinner" onClick={navigateToDinner}>Akşam Yemeği</a></li>}
            </ul>
          </div>
        </nav>
        <ReservationPage 
          checkIn={checkInDate}
          checkOut={checkOutDate}
          adults={parseInt(adultsCount) || 2}
          children={childrenAges.length}
          childrenAges={childrenAges}
          autoSearch={checkInDate !== '' && checkOutDate !== ''}
        />
      </>
    );
  }

  if (currentPage === 'calendar') {
    return (
      <>
        <nav className="navbar">
          <div className="container">
            <div className="nav-brand" onClick={navigateToHome} style={{ cursor: 'pointer' }}>
              <img src="/logo.png" alt="Otel Logo" className="logo" />
              Villa Polikne
            </div>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              ☰
            </button>
            <ul className={`nav-menu ${menuOpen ? 'active' : ''}`}>
              <li><a href="#home" onClick={navigateToHome}>Ana Sayfa</a></li>
              <li><a href="#our-rooms" onClick={navigateToOurRooms}>Odalarımız</a></li>
              <li><a href="#reservation" onClick={navigateToReservationDirect}>Rezervasyon Yap</a></li>
              <li><a href="#calendar" onClick={navigateToCalendar} style={{ color: '#d4af37' }}>Oda Takvimi</a></li>
              {sunbedWebEnabled && <li><a href="#sunbed" onClick={navigateToSunbed}>Şezlong Kiralama</a></li>}
              <li><a href="#breakfast" onClick={navigateToBreakfast}>Kahvaltı</a></li>
              {dinnerWebEnabled && <li><a href="#dinner" onClick={navigateToDinner}>Akşam Yemeği</a></li>}
            </ul>
          </div>
        </nav>
        <div className="calendar-page">
          <div className="container">
            <h1 className="calendar-page-title">Oda Müsaitlik Takvimi</h1>
            <p className="calendar-page-subtitle">Odaları takvim üzerinden inceleyin, müsait tarihe tıklayarak rezervasyon yapın</p>
            <MultiRoomCalendar onReservationSelect={handleHomeCalendarReservationSelect} />
          </div>
        </div>
        {showQuickBooking && quickBookingData && (
          <BookingFormModal
            isOpen={showQuickBooking}
            onClose={() => setShowQuickBooking(false)}
            source="home-calendar"
            roomId={quickBookingData.roomId}
            roomName={quickBookingData.roomName}
            checkInDate={quickBookingData.checkInDate}
            checkOutDate={quickBookingData.checkOutDate}
            adults={quickBookingData.adults}
            children={quickBookingData.children}
            childAges={quickBookingData.childAges}
            totalPrice={quickBookingData.totalPrice}
            nights={quickBookingData.nights}
          />
        )}
      </>
    );
  }

  if (currentPage === 'dinner') {
    if (!dinnerWebEnabled) {
      return (
        <div className="App" style={{ padding: '3rem 1rem', textAlign: 'center' }}>
          <h2>Akşam yemeği online satışı şu anda kapalıdır.</h2>
          <button className="btn-feature" onClick={navigateToHome} style={{ marginTop: '1rem' }}>Ana Sayfaya Dön</button>
        </div>
      );
    }
    return (
      <>
        <nav className="navbar">
          <div className="container">
            <div className="nav-brand" onClick={navigateToHome} style={{ cursor: 'pointer' }}>
              <img src="/logo.png" alt="Otel Logo" className="logo" />
              Villa Polikne
            </div>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              ☰
            </button>
            <ul className={`nav-menu ${menuOpen ? 'active' : ''}`}>
              <li><a href="#home" onClick={navigateToHome}>Ana Sayfa</a></li>
              <li><a href="#our-rooms" onClick={navigateToOurRooms}>Odalarımız</a></li>
              <li><a href="#reservation" onClick={navigateToReservationDirect}>Rezervasyon Yap</a></li>
              <li><a href="#calendar" onClick={navigateToCalendar}>Oda Takvimi</a></li>
              {sunbedWebEnabled && <li><a href="#sunbed" onClick={navigateToSunbed}>Şezlong Kiralama</a></li>}
              <li><a href="#breakfast" onClick={navigateToBreakfast}>Kahvaltı</a></li>
              {dinnerWebEnabled && <li><a href="#dinner" onClick={navigateToDinner} style={{ color: '#d4af37' }}>Akşam Yemeği</a></li>}
            </ul>
          </div>
        </nav>
        <DinnerBooking />
      </>
    );
  }

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="navbar">
        <div className="container">
          <div className="nav-brand" onClick={() => scrollToSection('home')} style={{ cursor: 'pointer' }}>
            <img src="/logo.png" alt="Otel Logo" className="logo" />
            Villa Polikne
          </div>
          <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
            ☰
          </button>
          <ul className={`nav-menu ${menuOpen ? 'active' : ''}`}>
            <li><a href="#home" onClick={() => scrollToSection('home')}>Ana Sayfa</a></li>
            <li><a href="#our-rooms" onClick={navigateToOurRooms}>Odalarımız</a></li>
            <li><a href="#reservation" onClick={navigateToReservationDirect}>Rezervasyon Yap</a></li>
            <li><a href="#calendar" onClick={navigateToCalendar}>Oda Takvimi</a></li>
            {sunbedWebEnabled && <li><a href="#sunbed" onClick={navigateToSunbed}>Şezlong Kiralama</a></li>}
            <li><a href="#breakfast" onClick={navigateToBreakfast}>Kahvaltı</a></li>
            {dinnerWebEnabled && <li><a href="#dinner" onClick={navigateToDinner}>Akşam Yemeği</a></li>}
          </ul>
        </div>
      </nav>

      {/* Hero Section with Slider */}
      <section id="home" className="hero">
        <div className="slider-container">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`slide ${index === currentSlide ? 'active' : ''} ${slidesLoaded[index] ? 'img-loaded' : 'img-loading'}`}
              style={slidesLoaded[index] ? { backgroundImage: `url(${slide})` } : {}}
            />
          ))}
          
          {/* Hero Overlay - Form ve Content slider dışında */}
          <div className="hero-overlay">
            <div className="hero-content">
              <h1 className="hero-title">{homepageData?.header?.hero_title || 'Hayallerinizdeki Tatil Buradan Başlıyor'}</h1>
              {(homepageData?.header?.hero_subtitle !== '') && (
                <p className="hero-subtitle">{homepageData?.header?.hero_subtitle || 'Lüks, konfor ve huzurun buluşma noktası'}</p>
              )}
              <div className="hero-buttons">
                {sunbedWebEnabled && (
                  <button className="btn-secondary-hero" onClick={navigateToSunbed}>
                    Şezlong Kiralama
                  </button>
                )}
                <button className="btn-secondary-hero" onClick={navigateToBreakfast}>
                  Haftasonu Kahvaltı
                </button>
                {dinnerWebEnabled && (
                  <button className="btn-secondary-hero" onClick={navigateToDinner}>
                    Akşam Yemeği
                  </button>
                )}
              </div>
            </div>

            {/* Yatay Arama Çubuğu - Alt kısım */}
            <div className="hero-search-bar" ref={homeDatePickerRef}>
              <div className="hsb-card">
                {/* Tarih alanı - tıklanınca takvim açılır */}
                <div
                  className={`hsb-field hsb-field-date${showHomeDatePicker ? ' hsb-field-active' : ''}`}
                  onClick={() => setShowHomeDatePicker(v => !v)}
                >
                  <span className="hsb-label">📅 Tarih Seçin</span>
                  <span className="hsb-date-display">
                    {checkInDate && checkOutDate
                      ? `${new Date(checkInDate + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} → ${new Date(checkOutDate + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}`
                      : checkInDate
                      ? `${new Date(checkInDate + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} → çıkış?`
                      : 'Giriş – Çıkış tarihi seçin'}
                  </span>
                </div>
                <div className="hsb-divider" />
                <div className="hsb-field hsb-field-sm">
                  <span className="hsb-label">Yetişkin</span>
                  <select className="hsb-input" value={adultsCount} onChange={handleAdultsCountChange}>
                    {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} Kişi</option>)}
                  </select>
                </div>
                <div className="hsb-divider" />
                <div className="hsb-field hsb-field-sm">
                  <span className="hsb-label">Çocuk</span>
                  <select className="hsb-input" value={childrenCount} onChange={handleChildrenCountChange}>
                    {[0,1,2,3,4].map(n => <option key={n} value={n}>{n === 0 ? 'Yok' : `${n} Çocuk`}</option>)}
                  </select>
                </div>
                <button
                  className="hsb-btn"
                  onClick={navigateToReservationWithSearch}
                  disabled={!checkInDate || !checkOutDate}
                >
                  Oda Ara
                </button>
              </div>

              {/* Tarih Takvimi Dropdown - Ekran ortasında fixed overlay */}
              {showHomeDatePicker && (
                <>
                  <div
                    className="hsb-calendar-overlay"
                    onClick={() => setShowHomeDatePicker(false)}
                  />
                  <div className="hsb-calendar-dropdown">
                    <div className="hsb-calendar-dropdown-header">
                      <span>Tarih Seçin</span>
                      <button onClick={() => setShowHomeDatePicker(false)}>✕</button>
                    </div>
                    <ReservationCalendar
                      onDateSelect={(checkIn, checkOut) => {
                        setCheckInDate(checkIn);
                        setCheckOutDate(checkOut);
                        setShowHomeDatePicker(false);
                      }}
                      initialCheckIn={checkInDate}
                      initialCheckOut={checkOutDate}
                    />
                  </div>
                </>
              )}

              {childrenCount > 0 && (
                <div className="hsb-ages">
                  <span className="hsb-ages-label">Çocuk yaşları:</span>
                  {childrenAges.map((age, i) => (
                    <div key={i} className="hsb-age-group">
                      <label>{i + 1}. çocuk</label>
                      <select value={age} onChange={e => handleChildAgeChange(i, parseInt(e.target.value))}>
                        {Array.from({length: 18}, (_, y) => <option key={y} value={y}>{y} yaş</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about">
        <div className="container">
          <h2 className="section-title">
            {homepageData?.about?.title || 'Hakkımızda'}
          </h2>
          <div className="about-content">
            <div className="about-text">
              {homepageData?.about ? (
                <>
                  {homepageData.about.paragraph1 && <p>{homepageData.about.paragraph1}</p>}
                  {homepageData.about.paragraph2 && <p>{homepageData.about.paragraph2}</p>}
                </>
              ) : (
                <>
                  <p>
                    Villa Polikne, 2010 yılından beri misafirlerine unutulmaz konaklama deneyimi sunmaktadır. 
                    Modern mimarisi, lüks odaları ve eşsiz hizmet anlayışıyla sektörün önde gelen otellerinden biridir.
                  </p>
                  <p>
                    Şehir merkezinde konumlanan otelimiz, iş ve tatil seyahatleriniz için mükemmel bir seçimdir. 
                    Profesyonel ekibimiz, konaklamanızı unutulmaz kılmak için 7/24 hizmetinizdedir.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Zigzag Section */}
      <section id="features" className="features-zigzag">
        <div className="container">
          {homepageData?.sections && homepageData.sections.length > 0 ? (
            homepageData.sections.map((section: any, index: number) => {
              const btnHandler = section.button_action ? getButtonHandler(section.button_action) : null;
              const isReverse = index % 2 === 1;
              return (
                <div key={section.id} className={`feature-row${isReverse ? ' reverse' : ''}`}>
                  <div className="feature-image">
                    {section.image_url ? (
                      <img src={section.image_url} alt={section.title} />
                    ) : (
                      <div className="placeholder"><span>{section.title} Görseli</span></div>
                    )}
                  </div>
                  <div className="feature-content">
                    <h2>{section.title}</h2>
                    <p>{section.content}</p>
                    {btnHandler && section.button_label && (
                      <button className="btn-feature" onClick={btnHandler}>{section.button_label}</button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <>
          {/* 1. Villamız - Resim Solda */}
          <div className="feature-row">
            <div className="feature-image">
              <img src="/polikne1.jpg" alt="Villa Polikne" />
            </div>
            <div className="feature-content">
              <h2>Villamız</h2>
              <p>
                1900'lü yılların başında inşa edilen tarihi villamız, Osmanlı ve Akdeniz mimarisinin 
                eşsiz birleşimini yansıtmaktadır. Özenle restore edilmiş yapımız, geçmişin zarafetini 
                modern konforla buluşturarak size unutulmaz bir konaklama deneyimi sunuyor.
              </p>
              <button className="btn-feature" onClick={navigateToOurRooms}>Daha Fazla</button>
            </div>
          </div>

          {/* 2. Plajımız - Resim Sağda */}
          <div className="feature-row reverse">
            <div className="feature-image">
              <img src="/polikne7.jpg" alt="Plajımız" />
            </div>
            <div className="feature-content">
              <h2>Plajımız</h2>
              <p>
                Masmavi Ege'nin kıyısında yer alan özel plajımızda güneşin ve denizin tadını çıkarın. 
                Şezlong ve şemsiye hizmetimiz, beach bar'ımız ve su sporları aktivitelerimizle 
                mükemmel bir sahil deneyimi sizleri bekliyor.
              </p>
              {sunbedWebEnabled && <button className="btn-feature" onClick={navigateToSunbed}>Şezlong Kirala</button>}
            </div>
          </div>

          {/* 3. Odalarımız - Resim Solda */}
          <div className="feature-row">
            <div className="feature-image">
              <img src="/polikne5.jpg" alt="Odalarımız" />
            </div>
            <div className="feature-content">
              <h2>Odalarımız</h2>
              <p>
                Her biri özenle tasarlanmış odalarımız, konfor ve estetiği bir arada sunuyor. 
                Deniz manzaralı süitlerden bahçe görünümlü standart odalara kadar geniş seçeneklerimizle 
                hayallerinizdeki tatili yaşayın.
              </p>
              <button className="btn-feature" onClick={navigateToOurRooms}>Odaları İncele</button>
            </div>
          </div>

          {/* 4. Kahvaltımız - Resim Sağda */}
          <div className="feature-row reverse">
            <div className="feature-image placeholder">
              <span>Kahvaltı Görseli</span>
            </div>
            <div className="feature-content">
              <h2>Kahvaltımız</h2>
              <p>
                Güne enerji dolu başlamanız için hazırlanan zengin açık büfe kahvaltımız, 
                yerel lezzetlerden uluslararası seçeneklere kadar geniş bir yelpaze sunuyor. 
                Taze sıkılmış meyve suları, ev yapımı börekler ve organik ürünler sizi bekliyor.
              </p>
              <button className="btn-feature" onClick={navigateToBreakfast}>Kahvaltı Rezervasyonu</button>
            </div>
          </div>

          {/* 5. Restoranımız - Resim Solda */}
          <div className="feature-row">
            <div className="feature-image">
              <img src="/polikne6.jpg" alt="Restoranımız" />
            </div>
            <div className="feature-content">
              <h2>Restoranımız</h2>
              <p>
                Deniz manzaralı restoranımızda Ulaş Şefimizin elinden, ustalıkla hazırladığı Ege ve Akdeniz 
                mutfağının en seçkin lezzetlerini tadın. Taze deniz ürünleri, mevsim sebzeleri ve 
                özel tariflerimizle damak zevkinize hitap ediyoruz.
              </p>
              {dinnerWebEnabled && <button className="btn-feature" onClick={navigateToDinner}>Akşam Yemeği</button>}
            </div>
          </div>
            </>
          )}
        </div>
      </section>

      {/* Gallery Section - Temporarily hidden */}
      {false && (
      <section id="gallery" className="gallery">
        <div className="container">
          <h2 className="section-title">Galeri</h2>
          <p className="section-subtitle">Otelimizden görüntüler</p>
          <div className="gallery-grid">
            <div className="gallery-item gallery-item-1">
              <div className="gallery-overlay">Lobimiz</div>
            </div>
            <div className="gallery-item gallery-item-2">
              <div className="gallery-overlay">Havuz</div>
            </div>
            <div className="gallery-item gallery-item-3">
              <div className="gallery-overlay">Restoran</div>
            </div>
            <div className="gallery-item gallery-item-4">
              <div className="gallery-overlay">SPA</div>
            </div>
            <div className="gallery-item gallery-item-5">
              <div className="gallery-overlay">Deluxe Oda</div>
            </div>
            <div className="gallery-item gallery-item-6">
              <div className="gallery-overlay">Manzara</div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>{homepageData?.header?.site_name || 'Villa Polikne'}</h3>
              <p>{homepageData?.footer?.tagline || 'Lüks ve konforun adresi'}</p>
              {homepageData?.contact?.address && (
                <p style={{ marginTop: '0.5rem', fontSize: '0.88rem', opacity: 0.85 }}>
                  📍 {homepageData.contact.address}
                </p>
              )}
              {homepageData?.contact?.contact_phone && (
                <p style={{ fontSize: '0.88rem', opacity: 0.85 }}>
                  📞 {homepageData.contact.contact_phone}
                </p>
              )}
              {homepageData?.contact?.contact_email && (
                <p style={{ fontSize: '0.88rem', opacity: 0.85 }}>
                  ✉️ {homepageData.contact.contact_email}
                </p>
              )}
            </div>
            <div className="footer-section">
              <h4>Hızlı Linkler</h4>
              <ul>
                <li><a href="#reservation-lookup" onClick={navigateToReservationLookup}>🔍 Rezervasyonumu Sorgula</a></li>
                <li><a href="#admin" onClick={navigateToAdmin}>🔐 Admin</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Sosyal Medya</h4>
              <div className="social-links">
                {(() => {
                  const igUrl = homepageData?.footer?.instagram_url || 'https://www.instagram.com/villapolikne';
                  return igUrl ? (
                    <a href={igUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="instagram-link">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                      </svg>
                    </a>
                  ) : null;
                })()}
                {homepageData?.footer?.facebook_url && (
                  <a href={homepageData.footer.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" style={{ marginLeft: '0.5rem' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{homepageData?.footer?.copyright_text || `© ${new Date().getFullYear()} Villa Polikne. Tüm hakları saklıdır.`}</p>
            {homepageData?.policies && homepageData.policies.length > 0 && (
              <div style={{ marginTop: '0.7rem', display: 'flex', flexWrap: 'wrap', gap: '0.3rem 1rem', justifyContent: 'center' }}>
                {homepageData.policies.map((p: any) => (
                  <button
                    key={p.policy_key}
                    onClick={() => openPolicy(p.policy_key, p.title)}
                    style={{
                      background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
                      cursor: 'pointer', fontSize: '0.82rem', padding: '0.1rem 0',
                      textDecoration: 'underline', textUnderlineOffset: '2px',
                    }}
                  >
                    {p.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </footer>

      {/* Policy Modal */}
      {policyModal && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={() => setPolicyModal(null)}
        >
          <div
            style={{
              backgroundColor: '#fff', borderRadius: '12px', maxWidth: '700px', width: '100%',
              maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '1.2rem 1.5rem', borderBottom: '1px solid #eee',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', color: '#2c3e50' }}>{policyModal.title}</h2>
              <button
                onClick={() => setPolicyModal(null)}
                style={{
                  background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer',
                  color: '#666', lineHeight: 1, padding: '0.2rem 0.5rem',
                }}
              >×</button>
            </div>
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              {policyModal.content ? (
                <div
                  style={{ lineHeight: 1.7, color: '#444', whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}
                  dangerouslySetInnerHTML={{ __html: policyModal.content }}
                />
              ) : (
                <p style={{ color: '#888', fontStyle: 'italic' }}>İçerik henüz eklenmemiştir.</p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Booking Form Modal - Takvimden direkt rezervasyon */}
      {showQuickBooking && quickBookingData && (
        <BookingFormModal
          isOpen={showQuickBooking}
          onClose={() => setShowQuickBooking(false)}
          source="home-calendar"
          roomId={quickBookingData.roomId}
          roomName={quickBookingData.roomName}
          checkInDate={quickBookingData.checkInDate}
          checkOutDate={quickBookingData.checkOutDate}
          adults={quickBookingData.adults}
          children={quickBookingData.children}
          childAges={quickBookingData.childAges}
          totalPrice={quickBookingData.totalPrice}
          nights={quickBookingData.nights}
        />
      )}
    </div>
  );
}

export default App;
